import { isFunction as _isFunc, trimStart } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { FormGroup, InputGroup, Label } from 'reactstrap';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import { change, Field, reduxForm } from 'redux-form';
import {
  FormRef,
  getUpdatedTextWithEmoji,
} from '../../../../../../../common/components/EmojiPicker/emoji-picker-helpers';
import { EmojiPicker } from '../../../../../../../common/components/EmojiPicker/EmojiPicker';
import Button from '../../../../../../../common/buttons/DeprecatedButton/DeprecatedButton';
import { ENTER_CODE } from '@mm/constants/keys-codes';
import { getWelcomePage } from '../../../../../../../common/helpers/utils';
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '../../../../../../../common/modals/modal-helpers';
import '../../../../../../../common/scss/input.scss';
import ButtonModel, {
  ButtonType,
  UrlButtonConfig,
  PageButtonConfig,
  PhoneNumberButtonConfig,
  HandoverButtonConfig,
} from '@mm/state/model/ButtonModel';
import PageModel from '@mm/state/model/PageModel';
import WidgetModel from '@mm/state/model/WidgetModel';
import { StoreState } from '@mm/state/store-interfaces';
import { ITEM, TEXT } from '@mm/constants/widgets';
import ButtonField from '../ButtonField/ButtonField';
import {
  ButtonConfigFormTypes,
  MAP_ENTITY,
  ReduxFormInput,
} from '../constants';
import TypePicker from '../TypePicker/TypePicker';
import './ButtonConfigForm.scss';
import { ButtonTagsForm } from './ButtonTagsForm';
import { validateButtonConfigForm } from './validations';
import { FormValues } from '@mm/state/interfaces/form';

type OuterProps = {
  button: ButtonModel;
  buttonIndex: number;
  valid: boolean;
  pages: PageModel[];
  onCreateButton: (button: ButtonModel) => void;
  onUpdateButton: (buttonIndex: number, button: ButtonModel) => void;
  closeModal: () => void;
  widget: WidgetModel;
};

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

type Props = OuterProps & InnerProps;

const REDUX_FORM_NAME = 'buttonConfig';

const checkFormType = (type: string, formValues?: FormValues): boolean => {
  return !!(formValues && formValues.type === type);
};

const getDefaultState = (pages: PageModel[]) => {
  const page = getWelcomePage(pages);

  return {
    page: page && page.id,
    phone: '',
    url: '',
    appId: '',
    share: '',
    tags: [],
  };
};

const setInitialValues = ({ pages, changeFieldValue }) => {
  const initialState = getDefaultState(pages);
  Object.entries(initialState).forEach(
    ([fieldName, value]) =>
      fieldName !== 'tags' && changeFieldValue(fieldName, value),
  );
};

const createClick = (props: Props) => {
  const { formValues, buttonIndex, onCreateButton, onUpdateButton } = props;

  if (formValues) {
    const buttonFactory = MAP_ENTITY[formValues.type];
    const buttonUnmapped = _isFunc(buttonFactory) ? buttonFactory(props) : null;
    const isShareButton = checkFormType(ButtonType.SHARE, formValues);

    const button = isShareButton
      ? { ...buttonUnmapped, name: 'Share' }
      : buttonUnmapped;

    const buttonIndexNumber = Number(buttonIndex);
    if (buttonIndex === -1) {
      onCreateButton(button);
    } else {
      onUpdateButton(buttonIndexNumber, button);
    }
  }
};

const ButtonConfigForm = (props: Props) => {
  const {
    closeModal,
    buttonIndex,
    valid,
    formValues,
    pages,
    pageGroups,
    changeFieldValue,
    widget,
    button,
  } = props;

  const [nameInputRef, setNameInputRef] = useState<FormRef | null>(null);

  const formTags = (formValues && formValues.tags) || [];

  const onButtonNameInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.target.value = trimStart(e.target.value);
    },
    [],
  );

  const onTagAdd = useCallback(
    (tag: { name: string }) =>
      changeFieldValue('tags', Array.from(new Set([tag].concat(formTags)))),
    [formTags],
  );

  const onTagRemove = useCallback(
    (tag: { name: string }) =>
      changeFieldValue(
        'tags',
        formTags.filter(e => e.name !== tag.name),
      ),
    [formTags],
  );

  const onTypeChange = useCallback(() => {
    setInitialValues({ pages, changeFieldValue });
  }, [pages, changeFieldValue]);

  const onCreateButtonLocal = () => createClick(props);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLFormElement>) => {
      if (e.keyCode === ENTER_CODE && valid) {
        createClick(props);
        e.preventDefault();
      }
    },
    [valid],
  );

  const hasButton = Number(buttonIndex) !== -1;

  const updateInsertEmoji = (emoji: string) => {
    if (formValues) {
      const { name } = formValues;

      if (nameInputRef) {
        const newText = getUpdatedTextWithEmoji(name, emoji, nameInputRef);
        changeFieldValue('name', newText);
      }
    }
  };

  useEffect(() => {
    const welcomePage = getWelcomePage(pages);
    changeFieldValue('type', button.type);
    changeFieldValue('name', button.name || '');
    changeFieldValue('url', (button.config as UrlButtonConfig).url || '');
    changeFieldValue(
      'appId',
      (button.config as HandoverButtonConfig).appId || '',
    );
    changeFieldValue(
      'page',
      (button.config as PageButtonConfig).pageId !== -1
        ? (button.config as PageButtonConfig).pageId
        : welcomePage && welcomePage.id,
    );
    changeFieldValue(
      'phone',
      (button.config as PhoneNumberButtonConfig).phoneNumber || '',
    );
    changeFieldValue('tags', button.tags || []);
  }, []);
  let filteredButtonTypes = ButtonConfigFormTypes;
  if (widget) {
    // Gallery and List widgets have type ITEM ('entry').
    // And currently these two types are the only that support sharing.
    if (
      widget.type !== ITEM ||
      (widget.buttons &&
        widget.buttons.some(b => b.type === ButtonType.SHARE) &&
        button.type !== ButtonType.SHARE)
    ) {
      filteredButtonTypes = filteredButtonTypes.filter(
        b => b !== ButtonType.SHARE,
      );
    }
    // Only TEXT widgets support the handover button
    if (widget.type !== TEXT) {
      filteredButtonTypes = filteredButtonTypes.filter(
        b => b !== ButtonType.HANDOVER,
      );
    }
  }

  const isShareButton = checkFormType(ButtonType.SHARE, formValues);
  const allowsTags =
    checkFormType(ButtonType.PAGE, formValues) ||
    checkFormType(ButtonType.URL, formValues);

  return (
    <div className="button-config__button-config-modal">
      <ModalHeader className="button-config__modal-header">
        {hasButton ? 'Edit Button' : 'Add Button'}
      </ModalHeader>
      <ModalBody className="button-config__body">
        <form onKeyDown={onKeyDown}>
          <FormGroup>
            <Label for="button-name">Button Name</Label>
            <InputGroup
              className="button-config__input button-config__name-input"
              onInput={onButtonNameInput}
            >
              <Field
                className="button-config__name-field"
                name="name"
                type="text"
                id="button-name"
                component={ReduxFormInput}
                maxLength={20}
                disabled={isShareButton}
                isShare={isShareButton}
                inputRef={setNameInputRef}
                actualRef={nameInputRef}
                placeholder="Enter button name"
              />
              {!isShareButton && (
                <EmojiPicker
                  className="button-config__emoji"
                  onEmojiPicked={updateInsertEmoji}
                />
              )}
            </InputGroup>
          </FormGroup>
          <FormGroup>
            <Field
              name="type"
              component={TypePicker}
              className="button-config__type-button"
              onChange={onTypeChange}
              types={filteredButtonTypes}
            />
          </FormGroup>
          <FormGroup className="button-config__type-button-wr">
            <ButtonField
              type={formValues && formValues.type}
              pages={pages}
              pageGroups={pageGroups}
            />
          </FormGroup>
        </form>
        {allowsTags && (
          <FormGroup>
            <ButtonTagsForm
              title="Add a Tag to contacts who click this button"
              tags={formTags}
              kind="add_tags"
              onTagAdd={onTagAdd}
              onTagRemove={onTagRemove}
            />
          </FormGroup>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          red
          invert
          onClick={closeModal}
          className="button-config__footer-btn"
        >
          Cancel
        </Button>
        <Button
          blue
          onClick={onCreateButtonLocal}
          className="button-config__footer-btn"
          disabled={isShareButton ? false : !valid}
        >
          {hasButton ? (
            'Update Button'
          ) : (
            <span>
              <i className="fa fa-plus" />
              &nbsp; Add Button
            </span>
          )}
        </Button>
      </ModalFooter>
    </div>
  );
};

const mapStateToProps = (state: StoreState) => {
  const formValues = state.form.buttonConfig
    ? state.form.buttonConfig.values
    : undefined;
  return {
    formValues,
    pages: state.page.dropdown,
    pageGroups: state.pageGroup.entities,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  changeFieldValue: (field: string, value: any) =>
    dispatch(change('buttonConfig', field, value)),
});

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
  reduxForm({ form: REDUX_FORM_NAME, validate: validateButtonConfigForm }),
);

export default enhance(ButtonConfigForm);
